import { useState, useMemo, useEffect } from 'react';
import staticResources from '../data/nil-resources.json';
import { Search, Star, FileText, Presentation, BookOpen, Calendar, ExternalLink, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  'case-study': 'Case Study',
  'one-pager': 'One-Pager',
  'sales-deck': 'Sales Deck',
  'article': 'Article',
  'white-paper': 'White Paper',
  'availability-report': 'Availability Report',
};

const FILTER_OPTIONS = [
  { key: 'all', label: 'All Resources' },
  { key: 'availability-report', label: 'Availability Reports' },
  { key: 'case-study', label: 'Case Studies' },
  { key: 'one-pager', label: 'One-Pagers' },
  { key: 'sales-deck', label: 'Sales Decks' },
  { key: 'article', label: 'Articles' },
];

const BADGE_GRADIENTS = {
  'case-study': 'linear-gradient(90deg, #4ECDC4, #2BA69F)',
  'one-pager': 'linear-gradient(135deg, #6B5CE7, #4ECDC4)',
  'sales-deck': 'linear-gradient(90deg, #6B5CE7, #9B8FFF)',
  'article': 'linear-gradient(90deg, #FF6B35, #FFD93D)',
  'white-paper': 'linear-gradient(90deg, #E040FB, #FF6B35)',
  'availability-report': 'linear-gradient(90deg, #0EA5E9, #4ECDC4)',
};

const TYPE_CHIP = {
  'case-study': { bg: '#E6FAF8', color: '#2BA69F' },
  'one-pager': { bg: '#EEF0FF', color: '#6B5CE7' },
  'sales-deck': { bg: '#F0EDFF', color: '#6B5CE7' },
  'article': { bg: '#FFF4ED', color: '#FF6B35' },
  'white-paper': { bg: '#FDF0FF', color: '#E040FB' },
  'availability-report': { bg: '#E0F2FE', color: '#0284C7' },
};

const ResourceCard = ({ resource, unlocked }) => {
  const chip = TYPE_CHIP[resource.type] || TYPE_CHIP['article'];
  const gradient = BADGE_GRADIENTS[resource.type] || BADGE_GRADIENTS['article'];
  const dateFormatted = new Date(resource.dateAdded).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const wrapperProps = unlocked
    ? { onClick: () => window.open(resource.contentFile, '_blank', 'noopener,noreferrer'), role: 'link', tabIndex: 0 }
    : {};

  return (
    <div
      {...wrapperProps}
      className={`flex flex-col group no-underline h-full ${unlocked ? 'cursor-pointer' : 'pointer-events-none'}`}
      data-testid={`public-resource-${resource.id}`}
    >
      <div
        className={`bg-white border border-[#eee] rounded-lg overflow-hidden flex-1 flex flex-col transition-all duration-200 ${
          unlocked ? 'hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(107,92,231,0.15)] cursor-pointer' : 'opacity-50 blur-[1px]'
        }`}
        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
      >
        <div className="h-1 w-full" style={{ background: gradient }} />
        <div className="p-5 flex-1 flex flex-col">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide self-start mb-2"
            style={{ background: chip.bg, color: chip.color }}
          >
            {resource.type === 'case-study' && resource.featured && <Star className="w-3 h-3" />}
            {TYPE_LABELS[resource.type]}
          </span>
          <h3
            className="text-[#1A1A1A] font-bold text-base leading-tight mb-2 group-hover:text-[#6B5CE7] transition-colors"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
          >
            {resource.title}
          </h3>
          <p className="text-[#666] text-sm leading-relaxed flex-1 line-clamp-3">{resource.description}</p>
        </div>
        <div className="px-5 py-3 border-t border-[#f0f0f0] flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-[#999]">
            <Calendar className="w-3 h-3" />
            {dateFormatted}
          </span>
          {unlocked && (
            <span className="flex items-center gap-1 text-sm font-semibold text-[#7B6FE8] group-hover:underline">
              View <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const PublicResourcesPage = () => {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('nil_access') === 'true');
  const [formData, setFormData] = useState({ full_name: '', organization: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    fetch(`${API}/public/resources`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setUploaded(data.map((r) => ({ ...r, contentFile: `${API}/public/resources/${r.id}/view` }))))
      .catch(() => {});
  }, []);

  const resources = useMemo(() => [...uploaded, ...staticResources], [uploaded]);

  const filtered = useMemo(() => {
    let list = activeFilter === 'all' ? resources : resources.filter((r) => r.type === activeFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((r) => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return new Date(b.dateAdded) - new Date(a.dateAdded);
    });
  }, [activeFilter, searchTerm, resources]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.organization || !formData.email) {
      setFormError('All fields are required');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const res = await fetch(`${API}/resource-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Submission failed');
      }
      localStorage.setItem('nil_access', 'true');
      setUnlocked(true);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        {/* Background grid of athlete photos */}
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 opacity-20">
          <div className="bg-cover bg-center" style={{ backgroundImage: "url('/nil-resources/photos/photo-basketball.png')" }} />
          <div className="bg-cover bg-center" style={{ backgroundImage: "url('/nil-resources/photos/photo-soccer.jpg')" }} />
          <div className="bg-cover bg-center hidden md:block" style={{ backgroundImage: "url('/nil-resources/photos/photo-tennis-teal.jpg')" }} />
          <div className="bg-cover bg-center hidden md:block" style={{ backgroundImage: "url('/nil-resources/photos/photo-tennis-topview.png')" }} />
        </div>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/70 to-[#0A0A0A]" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-12 pb-16 md:pt-16 md:pb-24">
          {/* Logo */}
          <img
            src="/nil-resources/logos/logo-white-transparent.png"
            alt="Players Health"
            className="h-10 md:h-12 mb-12"
            data-testid="public-logo"
          />

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left: Copy */}
            <div>
              <p className="text-[#4ECDC4] font-semibold text-sm uppercase tracking-widest mb-3">
                Players Health NIL
              </p>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-[1.05]"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Your NIL<br />Resource Library
              </h1>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-8 max-w-lg">
                Everything your athletic department needs to understand, manage, and insure NIL risk —
                case studies, coverage guides, sales decks, and thought leadership — all in one place.
              </p>

              {/* Value Props */}
              <div className="space-y-4">
                {[
                  { color: '#4ECDC4', title: 'Case Studies', desc: 'See how top programs handle NIL risk' },
                  { color: '#6B5CE7', title: 'Coverage Guides', desc: 'Quick-reference one-pagers and overviews' },
                  { color: '#F59E0B', title: 'Market Briefings', desc: 'Stay ahead of NIL regulatory trends' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-2.5 h-2.5 mt-1.5 rounded-full flex-shrink-0" style={{ background: item.color }} />
                    <div>
                      <span className="text-white font-semibold text-sm">{item.title}</span>
                      <span className="text-[#94A3B8] text-sm ml-1">— {item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form or Success */}
            <div>
              {!unlocked ? (
                <div className="bg-white rounded-xl p-8 shadow-2xl" data-testid="lead-form-container">
                  <h2
                    className="text-[#1A1A1A] text-2xl font-bold mb-6"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
                  >
                    Access the Library
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#475569] mb-1">Full Name</label>
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="w-full px-4 py-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent transition"
                        placeholder="Your full name"
                        data-testid="lead-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#475569] mb-1">School / Organization</label>
                      <input
                        type="text"
                        value={formData.organization}
                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                        className="w-full px-4 py-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent transition"
                        placeholder="University or organization"
                        data-testid="lead-org-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#475569] mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent transition"
                        placeholder="you@school.edu"
                        data-testid="lead-email-input"
                      />
                    </div>
                    {formError && (
                      <p className="text-red-500 text-sm" data-testid="lead-form-error">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 rounded-lg font-semibold text-white text-sm transition-all duration-200 disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #6B5CE7, #4ECDC4)' }}
                      data-testid="lead-submit-btn"
                    >
                      {submitting ? 'Submitting...' : 'Get Access'}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-8 text-center" data-testid="access-granted">
                  <CheckCircle className="w-16 h-16 text-[#4ECDC4] mx-auto mb-4" />
                  <h2
                    className="text-white text-2xl font-bold mb-2"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
                  >
                    Access Granted
                  </h2>
                  <p className="text-[#94A3B8] text-sm">Browse the full library below. Click any card to view the resource.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Gradient divider ── */}
      <div className="h-1 w-full bg-gradient-to-r from-[#6B5CE7] via-[#4ECDC4] to-[#F59E0B]" />

      {/* ── RESOURCE LIBRARY ── */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h2
            className="text-white text-3xl md:text-4xl font-bold mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
          >
            NIL Resource Library
          </h2>
          <p className="text-[#94A3B8] text-base">Browse case studies, guides, decks, and more</p>
        </div>

        {/* Search */}
        {unlocked && (
          <div className="max-w-xl mx-auto mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search resources..."
                className="w-full pl-11 pr-4 py-3 bg-[#1A1A1A] border border-[#333] rounded-lg text-sm text-white placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent"
                data-testid="public-search-input"
              />
            </div>
          </div>
        )}

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 justify-center mb-10 pb-6 border-b border-[#222]">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setActiveFilter(opt.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                activeFilter === opt.key
                  ? 'text-white border-[#7B6FE8]'
                  : 'text-[#999] border-[#333] hover:border-[#7B6FE8] hover:text-white'
              }`}
              style={activeFilter === opt.key ? { background: '#7B6FE8' } : { background: 'transparent' }}
              data-testid={`public-filter-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((resource) => (
            <ResourceCard key={resource.id} resource={resource} unlocked={unlocked} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-[#444] mx-auto mb-4" />
            <p className="text-[#666]">No resources found</p>
          </div>
        )}

        <div className="text-sm text-[#666] text-center mt-8">
          Showing {filtered.length} of {resources.length} resources
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-[#222] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/nil-resources/logos/logo-white-transparent.png" alt="Players Health" className="h-6 opacity-60" />
          <p className="text-[#666] text-xs">&copy; {new Date().getFullYear()} Players Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicResourcesPage;
